import PropTypes from 'prop-types';
import React from 'react';
import { FormattedDate } from 'react-intl';

import { IconButton } from '@bibliocommons/base-buttons';
import { Edit as IconEdit, Plus as IconPlus } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import ContextMenu from 'app/components/admin/browse/shared/ContextMenu';
import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from 'app/components/shared/Table';
import { LOCALE_CODE } from 'app/constants/language';
import { categoryConfigSummaryShape } from 'app/shapes/admin/browse/categories';

import './CategoryConfigsList.scss';

const CategoryConfigsList = ({
  categoryConfigByLocaleCode,
  isSaving,
  localeCodes,
  mapLocaleCodeToLabel,
  onCreateCategoryConfig,
  onLocaleCodeDelete,
  onSelectCategoryConfig,
  onViewLocaleCode,
  topLevelCategoryLabel
}) => {
  return (
    <Table className="cp-admin-browse-categories-config-list">
      <TableHeader>
        <TableHeading>Language</TableHeading>
        <TableHeading>Creator</TableHeading>
        <TableHeading>Last updated</TableHeading>
        <TableHeading />
      </TableHeader>
      <TableBody>
        {localeCodes.map(localeCode => {
          const categoryConfig = categoryConfigByLocaleCode[localeCode];

          const { createdBy = '', updatedAt = '' } = categoryConfig || {};
          const isNew = !categoryConfig;

          const handleCreate = () => onCreateCategoryConfig(localeCode);
          const handleDelete = () => onLocaleCodeDelete(localeCode);
          const handleEdit = () => onSelectCategoryConfig(categoryConfig);
          const handleView = () => onViewLocaleCode(localeCode);

          const confirmDeleteModalMessage = `Are you sure you want to delete the ‘${mapLocaleCodeToLabel(
            localeCode
          )}’ ${topLevelCategoryLabel} category?`;

          return (
            <TableRow key={localeCode}>
              <TableCell className="table-cell">{mapLocaleCodeToLabel(localeCode)}</TableCell>
              <TableCell className="table-cell">{createdBy || ''}</TableCell>
              <TableCell className="table-cell">
                {updatedAt > 0 && <FormattedDate day="2-digit" month="long" value={updatedAt} year="numeric" />}
              </TableCell>
              <TableCell className="table-cell">
                <div className="actions">
                  {isNew ? (
                    <IconButton
                      className="action"
                      dataKey="action-create"
                      handleClick={handleCreate}
                      isSaving={isSaving}
                    >
                      <ScreenReaderMessage>Create</ScreenReaderMessage>
                      <IconPlus />
                    </IconButton>
                  ) : (
                    <>
                      <IconButton className="action" dataKey="action-edit" handleClick={handleEdit} isSaving={isSaving}>
                        <ScreenReaderMessage>Edit</ScreenReaderMessage>
                        <IconEdit />
                      </IconButton>
                      <ContextMenu
                        confirmDeleteModalHeading="Delete category"
                        confirmDeleteModalMessage={confirmDeleteModalMessage}
                        onDeleteClick={handleDelete}
                        onViewClick={handleView}
                      />
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

CategoryConfigsList.propTypes = {
  categoryConfigByLocaleCode: PropTypes.objectOf(categoryConfigSummaryShape).isRequired,
  isSaving: PropTypes.bool.isRequired,
  localeCodes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(LOCALE_CODE))).isRequired,
  mapLocaleCodeToLabel: PropTypes.func.isRequired,
  onCreateCategoryConfig: PropTypes.func.isRequired,
  onLocaleCodeDelete: PropTypes.func.isRequired,
  onViewLocaleCode: PropTypes.func.isRequired,
  onSelectCategoryConfig: PropTypes.func.isRequired,
  topLevelCategoryLabel: PropTypes.string.isRequired
};

export default CategoryConfigsList;
